import { useState } from "react";
import type { CapabilityStatus } from "../../types/openclaw";
import { CAPABILITIES, PROVIDERS, type CapabilityDef } from "./constants";
import styles from "./shared.module.css";

type Props = {
  // Server-authoritative per-capability coverage from
  // /api/admin/claws/{id}/ai-config (AiStatus.capabilities).
  capabilities: CapabilityStatus[];
  // The set of providers whose customer key is currently configured
  // (post-staged-changes). Used only for "Needs ... key" hint copy.
  configuredProviders: Set<string>;
};

type GroupStatus = "active" | "partial" | "inactive";

export const CapabilityList = ({ capabilities, configuredProviders }: Props): JSX.Element => {
  const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);

  const coverage = new Map<string, boolean>(
    capabilities.map((c) => [c.capability, c.covered]),
  );

  const groupStatus = (cap: CapabilityDef): GroupStatus => {
    const flags = cap.backendCapabilities.map((id) => coverage.get(id) === true);
    if (flags.every((f) => f)) return "active";
    if (flags.some((f) => f) && cap.partial) return "partial";
    return "inactive";
  };

  return (
    <div>
      {CAPABILITIES.map((cap) => {
        const st = groupStatus(cap);
        const dotClass =
          st === "active" ? styles.statusDotActive
          : st === "partial" ? styles.statusDotPartial
          : styles.statusDotInactive;
        const statusLabel = st === "active" ? "active" : st === "partial" ? "partial" : "inactive";
        const missingHints = cap.hintProviders
          .filter((p) => !configuredProviders.has(p))
          .map((p) => PROVIDERS.find((pr) => pr.id === p)?.label ?? p);
        return (
          <div key={cap.id} className={styles.capRow}>
            <div className={styles.capHeader}>
              <span
                className={`${styles.statusDot} ${dotClass}`}
                aria-label={statusLabel}
                role="img"
              />
              <strong>{cap.name}</strong>
              <button
                type="button"
                className={styles.tooltipBtn}
                onClick={() => setTooltipOpen(tooltipOpen === cap.id ? null : cap.id)}
              >
                ?
              </button>
            </div>
            {tooltipOpen === cap.id && <p className={styles.tooltip}>{cap.tooltip}</p>}
            {st === "partial" && cap.partial && <p className={styles.capDetail}>{cap.partial.message}</p>}
            {st === "inactive" && missingHints.length > 0 && (
              <p className={styles.capDetail}>Needs {missingHints.join(" or ")} key</p>
            )}
          </div>
        );
      })}
    </div>
  );
};
